/**
 * Feedback Service - Handles submission of user feedback to APIM
 */
import { IPublicClientApplication } from '@azure/msal-browser';
import logger from './logger';

export interface FeedbackRequest {
  session: string; // Required: session ID
  rating: number; // Required: 1-5 star rating
  feedback: string; // Optional: feedback text
}

export interface FeedbackResponse {
  success: boolean;
  message: string;
  feedback_id: string;
  user_id: string;
  session_id: string;
  rating: number;
  timestamp: string;
}

export interface FeedbackError {
  error: {
    code: string;
    message: string;
    type: string;
  };
}

/**
 * Submit feedback to APIM endpoint
 */
export async function submitFeedback(
  msalInstance: IPublicClientApplication,
  request: FeedbackRequest
): Promise<FeedbackResponse> {
  // Get access token
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    throw new Error('No authenticated user found');
  }

  const account = accounts[0];
  
  try {
    // Use the same scope as other API calls (file upload, chat, etc.)
    const tokenResponse = await msalInstance.acquireTokenSilent({
      scopes: [`${process.env.REACT_APP_CLIENT_ID || '262b88ff-8a8d-49fc-8485-017906bd3c29'}/user_impersonation`],
      account: account,
    });

    const accessToken = tokenResponse.accessToken;

    // Use the correct feedback endpoint without duplicate path
    const apimBaseUrl = process.env.REACT_APP_APIM_BASE_URL || 'https://navigator.ai.3m.com';
    const feedbackEndpoint = `${apimBaseUrl}/nav-api/api/feedback`;
    

    logger.log('[Feedback] Submitting feedback:', {
      endpoint: feedbackEndpoint,
      session: request.session,
      rating: request.rating,
      hasFeedbackText: !!request.feedback
    });

    // Log the full request body for debugging
    logger.log('[Feedback] Request body:', JSON.stringify(request, null, 2));

    // Make the API call with APIM trace headers for debugging
    const response = await fetch(feedbackEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/json'
      },
      body: JSON.stringify(request)
    });

    logger.log('[Feedback] Response status:', response.status, response.statusText);
    
    // Log APIM trace location header
    const traceLocation = response.headers.get('Ocp-Apim-Trace-Location');
    if (traceLocation) {
      logger.log('[Feedback] APIM Trace Location:', traceLocation);
      logger.log('[Feedback] Copy this URL to view the trace in APIM portal');
    }
    
    // Log all response headers for debugging
    const headers: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      headers[key] = value;
    });
    logger.log('[Feedback] Response headers:', headers);

    // Try to parse response, handle cases where body might not be JSON
    let responseData: any;
    const responseText = await response.text();
    logger.log('[Feedback] Raw response:', responseText);
    
    try {
      responseData = JSON.parse(responseText);
    } catch (e) {
      console.error('[Feedback] Failed to parse response as JSON:', e);
      throw new Error(`Server returned non-JSON response (${response.status}): ${responseText.substring(0, 200)}`);
    }

    if (!response.ok) {
      // Handle error response
      const error = responseData as FeedbackError;
      console.error('[Feedback] Error response:', error);
      throw new Error(error.error?.message || `Failed to submit feedback: ${response.status}`);
    }

    logger.log('[Feedback] Success:', responseData);
    return responseData as FeedbackResponse;

  } catch (error) {
    console.error('[Feedback] Submission failed:', error);
    throw error;
  }
}

/**
 * Helper to get the current session ID from Redux store
 * This should be called from a component that has access to the Redux store
 */
export function getCurrentSessionId(): string {
  // This will be passed from the component
  // Placeholder - actual implementation will get from Redux
  return '';
}
